import { z } from 'zod';

/*
 * PDF editing: annotations drawn in the viewer and flattened into the file by the server.
 *
 * Coordinates are in "page space": PDF points on the unrotated page, measured from the top-left
 * corner of the page's visible box (CropBox, falling back to MediaBox), x to the right and y down.
 * This space ignores both the page's own /Rotate and the viewer's rotation and zoom, so an
 * annotation stays attached to the same spot of the page content however it is displayed.
 */

export const PDF_ANNOTATION_TYPES = ['highlight', 'ink', 'rect', 'arrow', 'text'] as const;
export type PdfAnnotationType = (typeof PDF_ANNOTATION_TYPES)[number];

/** Highlights are translucent so the text underneath stays readable once flattened. */
export const PDF_HIGHLIGHT_OPACITY = 0.38;
/** Free text: line advance and first-baseline offset, both as multiples of the font size. */
export const PDF_TEXT_LINE_HEIGHT = 1.25;
export const PDF_TEXT_ASCENT = 0.86;
export const PDF_MAX_ANNOTATIONS = 2000;
export const PDF_MAX_INK_POINTS = 4000;

const coordinate = z.number().finite().min(-20_000).max(20_000);
const point = z.tuple([coordinate, coordinate]);
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const strokeWidth = z.number().finite().min(0.25).max(48);
const box = z.object({
  x: coordinate,
  y: coordinate,
  width: z.number().finite().positive().max(20_000),
  height: z.number().finite().positive().max(20_000),
});

const base = {
  id: z.string().min(1).max(64),
  /** 1-based page number. */
  page: z.number().int().min(1).max(100_000),
  color,
};

export const pdfAnnotation = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('highlight'), rect: box }),
  z.object({ ...base, type: z.literal('rect'), rect: box, strokeWidth }),
  z.object({ ...base, type: z.literal('ink'), points: z.array(point).min(1).max(PDF_MAX_INK_POINTS), strokeWidth }),
  z.object({ ...base, type: z.literal('arrow'), from: point, to: point, strokeWidth }),
  z.object({
    ...base,
    type: z.literal('text'),
    /**
     * Top-left corner of the text box as the page is displayed with its own /Rotate: text is
     * written upright for a reader looking at the page the way the document presents it.
     */
    x: coordinate,
    y: coordinate,
    fontSize: z.number().finite().min(4).max(144),
    text: z.string().min(1).max(5000),
  }),
]);
export type PdfAnnotation = z.infer<typeof pdfAnnotation>;
export type PdfPoint = z.infer<typeof point>;
export type PdfBox = z.infer<typeof box>;

export const PDF_SAVE_MODES = ['overwrite', 'copy'] as const;
export type PdfSaveMode = (typeof PDF_SAVE_MODES)[number];

const saveTarget = {
  fileId: z.uuid(),
  mode: z.enum(PDF_SAVE_MODES),
  /** Name of the copy; defaults to "<name> (düzenlendi).pdf". Ignored when overwriting. */
  name: z.string().trim().min(1).max(240).optional(),
  /** Version of the file the edit was made against; an overwrite of a newer version is refused. */
  expectedVersion: z.number().int().min(1).optional(),
};

export const pdfAnnotateInput = z.object({
  ...saveTarget,
  annotations: z.array(pdfAnnotation).min(1).max(PDF_MAX_ANNOTATIONS),
});
export type PdfAnnotateInput = z.infer<typeof pdfAnnotateInput>;

export const pdfPageOperation = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rotate'),
    page: z.number().int().min(1),
    /** Clockwise, added to the page's current rotation. */
    degrees: z.union([z.literal(90), z.literal(180), z.literal(270), z.literal(-90)]),
  }),
  z.object({ type: z.literal('delete'), page: z.number().int().min(1) }),
]);
export type PdfPageOperation = z.infer<typeof pdfPageOperation>;

export const pdfPagesInput = z.object({
  ...saveTarget,
  /** Applied in order; each page number refers to the document as left by the previous operation. */
  operations: z.array(pdfPageOperation).min(1).max(500),
});
export type PdfPagesInput = z.infer<typeof pdfPagesInput>;

/**
 * Arrow shaft end and head triangle in page space, shared so the viewer draws exactly what the
 * server flattens. The shaft stops inside the head so its round cap never pokes past the tip.
 */
export function pdfArrowGeometry(from: PdfPoint, to: PdfPoint, strokeWidth: number) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) return { shaftEnd: to, head: [to, to, to] as PdfPoint[] };
  const ux = dx / length;
  const uy = dy / length;
  const size = Math.min(length, Math.max(8, strokeWidth * 3.5));
  const half = size * 0.5;
  const baseX = to[0] - ux * size;
  const baseY = to[1] - uy * size;
  const inset = size * 0.6;
  return {
    shaftEnd: [to[0] - ux * inset, to[1] - uy * inset] as PdfPoint,
    head: [to, [baseX - uy * half, baseY + ux * half], [baseX + uy * half, baseY - ux * half]] as PdfPoint[],
  };
}
